import Icon from "@/components/Icon/Icon";
import SectionHeading from "@/components/SectionHeading";
import Table from "@/components/Table";
import { renderEmptyCellState } from "@/components/Table/utils";
import { toFormat } from "@/lib/dateHelpers";
import type { ColumnDef } from "@tanstack/react-table";
import type { ApplicationBeneficiary } from "../queries";

interface BeneficiaryRow extends ApplicationBeneficiary {
  id: string;
  type: "Primary" | "Contingent";
}

function buildColumns(): ColumnDef<BeneficiaryRow, unknown>[] {
  return [
    {
      id: "name",
      header: "Name",
      meta: { headerClassName: "hide-header-in-column" },
      accessorFn: (row) => row.fullName,
      cell: ({ row, getValue }) => {
        const name = renderEmptyCellState(getValue());
        if (row.original.type !== "Primary") return name;
        return (
          <span className="inline-flex items-center gap-1">
            <Icon name="check" size={18} />
            {name}
          </span>
        );
      },
      minSize: 200
    },
    {
      id: "type",
      header: "Type",
      accessorFn: (row) => row.type,
      size: 112,
      minSize: 112
    },
    {
      id: "relationship",
      header: "Relationship",
      accessorFn: (row) => row.relationship,
      cell: ({ getValue }) => renderEmptyCellState(getValue()),
      minSize: 200
    },
    {
      id: "dateOfBirth",
      header: "Date of Birth",
      accessorFn: (row) => row.dateOfBirth,
      cell: ({ row }) =>
        renderEmptyCellState(row.original.dateOfBirth ? toFormat(row.original.dateOfBirth, "MMM dd, yyyy") : null),
      size: 112,
      minSize: 112
    },
    {
      id: "ssn",
      header: "SSN Last 4",
      accessorFn: (row) => row.ssnLast4,
      cell: ({ row }) => renderEmptyCellState(row.original.ssnLast4),
      size: 112,
      minSize: 112
    },
    {
      id: "percent",
      header: "Percent",
      accessorFn: (row) => row.percent,
      cell: ({ row }) => {
        const pct = row.original.percent;
        if (pct == null) return renderEmptyCellState(null);
        return `${pct}%`;
      },
      size: 112,
      minSize: 112
    }
  ];
}

interface Props {
  primaryBeneficiaries: ApplicationBeneficiary[] | null;
  contingentBeneficiaries: ApplicationBeneficiary[] | null;
}

export default function BeneficiariesSection({ primaryBeneficiaries, contingentBeneficiaries }: Props) {
  const columns = buildColumns();

  const rows: BeneficiaryRow[] = [
    ...(primaryBeneficiaries ?? []).map((b, i) => ({
      ...b,
      id: `primary-${i}`,
      type: "Primary" as const
    })),
    ...(contingentBeneficiaries ?? []).map((b, i) => ({
      ...b,
      id: `contingent-${i}`,
      type: "Contingent" as const
    }))
  ];

  return (
    <>
      <SectionHeading heading="Beneficiaries" body="View primary and secondary beneficiary information" />
      <Table<BeneficiaryRow> loading={false} data={rows} columns={columns} getRowId={(row) => row.id} />
    </>
  );
}
